import React from "react";
import GpuTelemetry from "../viz/GpuTelemetry";
import { WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { useI18n } from "../../i18n/I18nContext";

const Side = ({ tone, label, util, items, note, testid, mode }) => {
  const isBad = tone === "bad";
  const ColorIcon = isBad ? WarningCircle : CheckCircle;
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl p-7 sm:p-8 overflow-hidden h-full ${
        isBad ? "glass" : "glass-strong"
      }`}
      style={!isBad ? { boxShadow: "0 0 0 1px rgba(0,209,255,0.2), 0 24px 80px rgba(0,102,255,0.18)" } : {}}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ColorIcon
            size={16}
            weight="fill"
            className={isBad ? "text-[#E11D48]" : "text-success"}
          />
          <div className="mono text-[10px] uppercase tracking-[0.22em]" style={{ color: isBad ? "#E11D48" : "#00B85A" }}>
            {label}
          </div>
        </div>
        <div className={`mono text-[10px] uppercase tracking-widest ${isBad ? "text-muted" : "text-brand-cyan"}`}>
          {note}
        </div>
      </div>

      <div className="flex items-end gap-4 mb-6">
        <div
          className="font-heading font-black tracking-tighter text-6xl sm:text-7xl leading-none mono"
          style={{ color: isBad ? "#E11D48" : "#0099C9", textShadow: isBad ? "" : "0 0 24px rgba(0,209,255,0.45)" }}
        >
          {util}%
        </div>
        <div className="text-xs mono uppercase tracking-widest text-muted pb-2">GPU UTIL</div>
      </div>

      <GpuTelemetry mode={mode} testid={`${testid}-chart`} />

      <ul className="mt-6 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-muted">
            <span
              className="mt-1.5 w-1.5 h-1.5 rounded-full"
              style={{ background: isBad ? "#E11D48" : "#0099C9" }}
            />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const BeforeAfter = () => {
  const { t } = useI18n();

  return (
    <section data-testid="before-after-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="mono text-xs uppercase tracking-[0.22em] text-brand-cyan mb-3">
            {t.beforeAfter.sectionNum}
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            {t.beforeAfter.headlinePart1}
            <br />
            <span className="text-brand-cyan">{t.beforeAfter.headlinePart2}</span>
            <br />
            <span className="text-muted">{t.beforeAfter.headlinePart3}</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 relative">
          <Side
            tone="bad"
            mode="k8s"
            label={t.beforeAfter.beforeLabel}
            util={35}
            note={t.beforeAfter.beforeNote}
            testid="ba-before"
            items={t.beforeAfter.beforeItems}
          />
          <Side
            tone="good"
            mode="inboost"
            label={t.beforeAfter.afterLabel}
            util={85}
            note={t.beforeAfter.afterNote}
            testid="ba-after"
            items={t.beforeAfter.afterItems}
          />
          {/* Divider */}
          <div className="hidden md:block absolute top-10 bottom-10 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />
        </div>

        {/* Tech stack evolution band */}
        <div className="mt-16" data-testid="stack-evolution">
          <div className="text-center mb-8">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-2">
              {t.beforeAfter.evolutionOverline}
            </div>
            <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">
              {t.beforeAfter.evolutionTitle}
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-4 relative">
            {/* Connector arrow line — desktop only */}
            <div className="hidden md:block absolute top-1/2 left-[18%] right-[18%] h-px bg-gradient-to-r from-[#E11D48]/40 via-[#E0A800]/50 to-brand/70" />

            {t.beforeAfter.tiers.map((tier, idx) => (
              <StackTier
                key={tier.tier}
                tier={tier.tier}
                level={tier.level}
                desc={tier.desc}
                color={tier.color}
                dotColor={tier.dotColor || tier.color}
                index={idx + 1}
                highlight={tier.highlight}
                testid={`stack-${idx === 0 ? "k8s" : idx === 1 ? "rule" : "inboost"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const StackTier = ({ tier, level, desc, color, dotColor, index, highlight, testid }) => (
  <div
    data-testid={testid}
    className={`relative rounded-2xl p-6 sm:p-7 ${
      highlight ? "glass-strong" : "glass"
    } hover:-translate-y-1 transition-transform duration-300`}
    style={
      highlight
        ? { boxShadow: "0 0 0 1px rgba(0,80,230,0.2), 0 24px 60px rgba(0,80,230,0.18)" }
        : {}
    }
  >
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center mono text-xs font-bold"
        style={{
          background: highlight ? `${color}1A` : "#FFFFFF",
          color: color,
          border: `1.5px solid ${color}55`,
          boxShadow: highlight ? `0 0 16px ${dotColor}55` : "none",
        }}
      >
        0{index}
      </div>
      <div
        className="mono text-[10px] uppercase tracking-[0.22em]"
        style={{ color }}
      >
        {level}
      </div>
    </div>
    <div className="font-heading font-bold text-xl tracking-tight" style={{ color: highlight ? color : "#0A0F1E" }}>
      {tier}
    </div>
    <p className="mt-3 text-sm text-muted leading-relaxed">{desc}</p>
  </div>
);

export default BeforeAfter;
